import React from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormHelpCallout } from "../FormHelpCallout";
import type { CreateCustomerCapacityGroupData } from "../../../types/startup.types";

export interface CapacityGroupFormProps {
  onSubmit: (data: CreateCustomerCapacityGroupData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  initialData?: Partial<CreateCustomerCapacityGroupData>;
  isLoading?: boolean;
  isEditing?: boolean;
}

export const CapacityGroupForm: React.FC<CapacityGroupFormProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  initialData,
  isLoading = false,
  isEditing = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    name: string;
    description?: string;
    maxCustomers: number;
  }>({
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      maxCustomers: initialData?.maxCustomers || 1000,
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) =>
        onSubmit({
          name: data.name,
          description: data.description,
          maxCustomers: Number(data.maxCustomers),
          startupId: "",
        }),
      )}
      className="space-y-4"
    >
      <FormHelpCallout
        learnMoreHref="/help/capacity-groups"
        learnMoreLabel="Capacity groups explained"
      >
        Create a group per shared location or resource. Then link customer
        projections to this group on their <strong className="text-white/75">Capacity</strong>{" "}
        tab — not on revenue plans.
      </FormHelpCallout>

      <FormField label="Location / group name" error={errors.name?.message}>
        <Input {...register("name", { required: "Name is required" })} placeholder="e.g., Main St office" />
      </FormField>
      <FormField label="Description (optional)" error={errors.description?.message}>
        <Input {...register("description")} placeholder="Shared capacity across projections at this site" />
      </FormField>
      <FormField
        label="Max customers"
        error={errors.maxCustomers?.message}
        helperText="Combined active customers across all projections linked to this group cannot exceed this number."
      >
        <Input
          type="number"
          {...register("maxCustomers", {
            required: "Max customers is required",
            min: { value: 1, message: "Must be at least 1" },
            valueAsNumber: true,
          })}
        />
      </FormField>
      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" size="md" disabled={isLoading} className="flex-1">
          {isLoading ? "Saving..." : "Save capacity group"}
        </Button>
        {isEditing && onDelete ? (
          <Button type="button" variant="danger" size="md" onClick={onDelete} disabled={isLoading}>
            Delete
          </Button>
        ) : null}
        <Button type="button" variant="secondary" size="md" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
